/**
 * AuthShell — shared visual frame for every auth screen (Login, Register,
 * Reset Password, Forgot Password) in the Strata x Framework design.
 *
 * Layout: split-screen feel. Left/full panel = Strata navy with the
 * stacked Strata logo, brand tagline, and a subtle architectural
 * gradient. Right side = the auth form on a clean white card. On
 * narrow viewports the navy half collapses and only the form card
 * remains, centred.
 *
 * Owns:
 *   - Strata-branded ambient background + stacked logo
 *   - Brand-message rotator (Built to Scale. ↔ Stop Managing Chaos.)
 *   - Glass card with hairline border + soft shadow
 *   - Autofill defense (Chrome/Safari override the cream-on-dark
 *     color with their own yellow/white background and dark text)
 *   - Shared button-hover lift used by every primary CTA in auth
 *
 * IMPORTANT: this is a presentational wrapper only. Auth logic
 * lives in AuthContext + each page's own handleSubmit.
 */
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

// Two rotating brand messages requested by ownership for the auth
// surface. The first is the long-standing "Built to Scale." promise;
// the second is the operator-pain hook. Rotation cadence matches the
// rest of the marketing site (~6s) and uses CSS opacity for a calm
// crossfade — no external animation lib, no layout shift.
const BRAND_MESSAGES = [
  {
    id: 'built-to-scale',
    leadWhite: 'Built to',
    leadAccent: 'Scale.',
    body:
      'The operating system for high-end design-build contractors. '
      + 'Designed by operators, for operators.',
  },
  {
    id: 'stop-managing-chaos',
    leadWhite: 'Stop Managing',
    leadAccent: 'Chaos.',
    body:
      'Get the operating system that runs your jobs, communicates '
      + 'with your clients, and scales with your business.',
  },
];
const BRAND_ROTATE_MS = 6000;

export default function AuthShell({ children, maxWidth = 440, testId }) {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setMsgIdx((i) => (i + 1) % BRAND_MESSAGES.length),
      BRAND_ROTATE_MS,
    );
    return () => clearInterval(t);
  }, []);
  const msg = BRAND_MESSAGES[msgIdx];
  return (
    <div
      data-testid={testId}
      className="min-h-screen flex relative overflow-hidden"
      style={{ background: 'var(--strata-navy, #0D1B2A)', fontFamily: 'var(--strata-font-body, Montserrat), system-ui, sans-serif' }}
    >
      {/* ─── Left brand panel (hidden on mobile) ─────────────────────── */}
      <div className="hidden lg:flex relative w-1/2 flex-col justify-between p-12 overflow-hidden" style={{ background: 'var(--strata-navy, #0D1B2A)' }}>
        {/* Architectural lines / glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-[0.10]"
               style={{ background: 'radial-gradient(circle, #4575A8 0%, transparent 65%)' }} />
          <div className="absolute bottom-[-20%] right-[-15%] w-[600px] h-[600px] rounded-full opacity-[0.06]"
               style={{ background: 'radial-gradient(circle, #5C8AC2 0%, transparent 70%)' }} />
          {/* Subtle grid pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="strata-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#E6E8EC" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#strata-grid)" />
          </svg>
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <span className="text-base font-bold tracking-[0.18em] uppercase" style={{ color: '#E6E8EC', fontFamily: 'var(--strata-font-display, "Exo 2"), system-ui, sans-serif' }}>
            Strata x Framework
          </span>
        </div>

        <div className="relative z-10 max-w-md">
          {/* iter-156dn — rotating brand message. Two operator-facing
              taglines crossfade every ~6s. The headline structure is
              identical across messages (white lead + accent-blue
              punctuation word) so the eye-tracking line doesn't
              shift between rotations. */}
          <div
            key={msg.id}
            data-testid={`auth-brand-msg-${msg.id}`}
            className="auth-brand-rotator"
          >
            <h2
              className="text-4xl font-bold mb-3 leading-tight"
              style={{
                color: '#FFFFFF',
                fontFamily: 'var(--strata-font-display, "Exo 2")',
                letterSpacing: '0.01em',
              }}
            >
              {msg.leadWhite}{' '}
              <span style={{ color: '#5C8AC2' }}>{msg.leadAccent}</span>
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(230,232,236,0.72)' }}>
              {msg.body}
            </p>
          </div>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-[1px] flex-1" style={{ background: 'rgba(230,232,236,0.12)' }} />
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: 'rgba(230,232,236,0.5)' }}>Built to stop the chaos</span>
            <div className="h-[1px] flex-1" style={{ background: 'rgba(230,232,236,0.12)' }} />
          </div>
        </div>

        <div className="relative z-10 text-[10px] uppercase tracking-[0.25em] flex items-end justify-between gap-6" style={{ color: 'rgba(230,232,236,0.4)' }}>
          <span>© 2026 Strata x Framework</span>
          {/* Powered-by partner mark — inverted to white for the navy panel,
              kept low-opacity so it reads as a credit rather than a primary
              brand element. */}
          <div className="flex flex-col items-end gap-1.5 opacity-80 hover:opacity-100 transition-opacity" data-testid="auth-partner-mark">
            <span className="text-[8px] tracking-[0.35em]" style={{ color: 'rgba(230,232,236,0.45)' }}>Powered by</span>
            <img
              src="/brand/elevate_to_greatness.png"
              alt="Elevate to Greatness"
              style={{
                height: 40,
                width: 'auto',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </div>
        </div>
      </div>

      {/* ─── Right form panel ────────────────────────────────────────── */}
      <div
        className="flex-1 flex items-center justify-center p-6 lg:p-10 relative"
        style={{ background: 'var(--strata-canvas, #E6E8EC)' }}
      >
        <div
          className="relative z-10 w-full flex flex-col items-center"
          style={{ maxWidth: `${maxWidth}px`, animation: 'authFadeIn 0.5s ease-out both' }}
        >
          {/* Brand mark — full primary logo (STRATA X FRAMEWORK).
              Sits above the form card on the light canvas for max contrast. */}
          <img
            src="/brand/primary_logo.svg"
            alt="Strata x Framework"
            className="mb-6"
            style={{ width: 280, height: 'auto' }}
            fetchPriority="high"
            decoding="async"
            data-testid="auth-brand-mark"
          />
          <style>{`
            @keyframes authFadeIn {
              from { opacity: 0; transform: translateY(12px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .auth-input:focus-within {
              border-color: var(--strata-steel, #4575A8) !important;
              box-shadow: 0 0 0 3px rgba(69,117,168,0.14);
            }
            /* Autofill defense — Chrome/Safari/Edge force their own light-bg
               + dark-text on autofilled inputs. We force ours back. */
            .auth-input input,
            .auth-input input:hover,
            .auth-input input:focus,
            .auth-input input:active,
            .auth-input input:-webkit-autofill,
            .auth-input input:-webkit-autofill:hover,
            .auth-input input:-webkit-autofill:focus,
            .auth-input input:-webkit-autofill:active {
              -webkit-text-fill-color: #111417 !important;
              caret-color: #111417 !important;
              color-scheme: light;
            }
            .auth-input input:-webkit-autofill,
            .auth-input input:-webkit-autofill:hover,
            .auth-input input:-webkit-autofill:focus,
            .auth-input input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
              box-shadow: 0 0 0 1000px #FFFFFF inset !important;
              transition: background-color 99999s ease-out, color 99999s ease-out;
            }
            .auth-btn { transition: all 0.18s ease; }
            .auth-btn:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 6px 16px rgba(69,117,168,0.30);
            }
            .auth-btn:active:not(:disabled) { transform: translateY(0); }
            .auth-link { transition: color 0.15s ease; }
            .auth-link:hover { color: var(--strata-navy, #0D1B2A) !important; }
            /* iter-156dn — rotating brand message crossfade. The
               component is re-mounted by React (key change) on each
               rotation, which retriggers the keyframe. No layout
               shift: opacity + tiny y-translate only. Respects
               prefers-reduced-motion. */
            @keyframes auth-brand-fade-in {
              from { opacity: 0; transform: translateY(6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .auth-brand-rotator {
              animation: auth-brand-fade-in 0.55s ease-out both;
              will-change: opacity, transform;
            }
            @media (prefers-reduced-motion: reduce) {
              .auth-brand-rotator { animation: none; }
            }
          `}</style>

          <div
            className="rounded-2xl p-7 lg:p-8 w-full"
            style={{
              background: '#FFFFFF',
              border: '1px solid var(--strata-border, #D5D8DD)',
              boxShadow: '0 1px 2px rgba(15,21,32,0.04), 0 12px 36px rgba(15,21,32,0.08)',
            }}
          >
            {children}
          </div>

          {/* Tagline below the card */}
          <p className="text-center text-[10px] uppercase tracking-[0.3em] mt-5" style={{ color: 'rgba(17,20,23,0.4)' }}>
            Structured · Precise · Modern
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * AuthHeader — title + subtitle pair used at the top of each auth view.
 * Kept as a sibling export so AuthShell stays purely presentational.
 */
export function AuthHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6">
      {children}
      <h1
        className="text-[26px] font-bold tracking-tight"
        style={{ color: 'var(--strata-navy, #0D1B2A)', fontFamily: 'var(--strata-font-display, "Exo 2"), sans-serif', letterSpacing: '0.01em' }}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="text-sm mt-1.5" style={{ color: 'var(--strata-text-secondary, #5C6370)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/**
 * AuthAlert — colored info/error/success/warn banner.
 */
export function AuthAlert({ kind = 'error', children, testId }) {
  const palette = {
    error:   { bg: 'rgba(180,83,83,0.10)', border: 'rgba(180,83,83,0.30)', text: '#9C3D3D' },
    success: { bg: 'rgba(74,124,89,0.10)', border: 'rgba(74,124,89,0.30)', text: '#3F6B4E' },
    warn:    { bg: 'rgba(196,165,97,0.12)', border: 'rgba(196,165,97,0.32)', text: '#8C6E2E' },
    info:    { bg: 'rgba(69,117,168,0.10)', border: 'rgba(69,117,168,0.30)', text: '#2F5685' },
  }[kind];
  return (
    <div
      data-testid={testId}
      role={kind === 'error' ? 'alert' : 'status'}
      className="p-3 rounded-md text-sm"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.text, fontFamily: 'var(--strata-font-body, Montserrat), sans-serif' }}
    >
      {children}
    </div>
  );
}

/**
 * AuthFootLink — bottom-of-card "Don't have an account? Sign up" link row.
 * Uses React Router <Link> for `to` paths so navigation stays in-SPA
 * (full <a href> would trigger a full page reload + re-bootstrap of
 * AuthContext, occasionally producing a flash of unauthenticated state).
 */
export function AuthFootLink({ prompt, label, to, onClick, testId }) {
  const linkProps = to
    ? { to, as: undefined }
    : { type: 'button', onClick };
  const Tag = to ? Link : 'button';
  return (
    <p className="text-center text-sm mt-2" style={{ color: 'var(--strata-text-muted, #8A8F99)' }}>
      {prompt}{' '}
      <Tag
        {...linkProps}
        data-testid={testId}
        className="auth-link font-semibold"
        style={{ color: 'var(--strata-steel, #4575A8)' }}
      >
        {label}
      </Tag>
    </p>
  );
}
